import * as THREE from 'three';
import type { PersonModel } from './library';

/** Car paint: instanceColor tints only the body panels (`carPaint` = 1). */
export function carPaintMaterial(): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.35, metalness: 0.35, envMapIntensity: 1.5, flatShading: true });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nattribute float carPaint;').replace(
      '#include <color_vertex>',
      `#include <color_vertex>
      #ifdef USE_INSTANCING_COLOR
        vColor.xyz = mix(color.xyz, color.xyz * instanceColor.xyz, carPaint);
      #endif`,
    );
  };
  m.customProgramCacheKey = () => 'carPaint';
  return m;
}

/**
 * The vertex-animation fetch: position (and normal) of this vertex at the instance's clip time,
 * interpolated between baked frames. Per instance, pedAnim = (seconds into the clip, clip 0 walk /
 * 1 idle, trousers index, hair * 4 + skin index).
 */
function vatChunk(p: PersonModel, withNormal: boolean): string {
  const frames = p.walk.frames + p.idle.frames;
  return `
  float vatClip = pedAnim.y;
  float vatN = vatClip < 0.5 ? ${p.walk.frames.toFixed(1)} : ${p.idle.frames.toFixed(1)};
  float vatSecs = vatClip < 0.5 ? ${p.walk.seconds.toFixed(4)} : ${p.idle.seconds.toFixed(4)};
  float vatStart = vatClip < 0.5 ? 0.0 : ${p.walk.frames.toFixed(1)};
  float vatF = fract(pedAnim.x / vatSecs) * vatN;
  float vatF0 = floor(vatF);
  float vatF1 = mod(vatF0 + 1.0, vatN);
  float vatT = vatF - vatF0;
  int vatX = gl_VertexID % ${p.width};
  int vatY = gl_VertexID / ${p.width};
  int vatR0 = int(vatStart + vatF0) * ${p.rows} + vatY;
  int vatR1 = int(vatStart + vatF1) * ${p.rows} + vatY;
  transformed = mix(texelFetch(uVat, ivec2(vatX, vatR0), 0).xyz, texelFetch(uVat, ivec2(vatX, vatR1), 0).xyz, vatT);
  ${withNormal ? `vatNormal = normalize(mix(texelFetch(uVat, ivec2(vatX, vatR0 + ${frames * p.rows}), 0).xyz, texelFetch(uVat, ivec2(vatX, vatR1 + ${frames * p.rows}), 0).xyz, vatT));` : ''}`;
}

// Linear albedos (the shader's colours are linear; sRGB-looking values washed out to grey in the sun).
const SKIN = 'vec3[4](vec3(0.6, 0.39, 0.28), vec3(0.38, 0.22, 0.14), vec3(0.16, 0.085, 0.05), vec3(0.5, 0.31, 0.2))';
const PANTS = 'vec3[4](vec3(0.1, 0.12, 0.2), vec3(0.06, 0.06, 0.07), vec3(0.42, 0.37, 0.29), vec3(0.28, 0.3, 0.34))';
const HAIR = 'vec3[4](vec3(0.02, 0.015, 0.012), vec3(0.09, 0.045, 0.02), vec3(0.45, 0.3, 0.12), vec3(0.05, 0.03, 0.02))';

export function personMaterial(p: PersonModel): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: false });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uVat = { value: p.vat };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nuniform highp sampler2D uVat;\nattribute float tint;\nattribute vec4 pedAnim;\nvec3 vatNormal;')
      .replace('#include <beginnormal_vertex>', `vec3 transformed;\n${vatChunk(p, true)}\nvec3 objectNormal = vatNormal;`)
      .replace('#include <begin_vertex>', '')
      .replace(
        '#include <color_vertex>',
        `#include <color_vertex>
        {
          vec3 base = color;
          int hs = int(pedAnim.w + 0.5);
          #ifdef USE_INSTANCING_COLOR
            if (tint > 0.5 && tint < 1.5) base = instanceColor;
          #endif
          if (tint > 1.5 && tint < 2.5) base = ${PANTS}[int(pedAnim.z + 0.5) % 4];
          if (tint > 2.5 && tint < 3.5) base = ${HAIR}[hs / 4 % 4];
          if (tint > 3.5) base = ${SKIN}[hs % 4];
          vColor.xyz = base;
        }`,
      );
  };
  m.customProgramCacheKey = () => `person:${p.name}`;
  return m;
}

/** Shadow-pass material with the same animation, so shadows walk too. */
export function personDepthMaterial(p: PersonModel): THREE.MeshDepthMaterial {
  const m = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uVat = { value: p.vat };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nuniform highp sampler2D uVat;\nattribute vec4 pedAnim;')
      .replace('#include <begin_vertex>', `vec3 transformed;\n${vatChunk(p, false)}`);
  };
  m.customProgramCacheKey = () => `personDepth:${p.name}`;
  return m;
}
